import type { SagaStep } from '@paguz/contracts';
import type { AdminPaymentIntentDetail, SagaStepDetail } from '../../api/admin-schemas.ts';

/**
 * Reglas de las acciones de operador (`06-backoffice.md` §4.4). La UI las usa para habilitar
 * botones y explicar por qué no aplican; el servidor vuelve a validarlas.
 */
export type OperatorActionKind = 'retry' | 'query-breb' | 'force-compensation' | 'resolve';

export interface OperatorAction {
  kind: OperatorActionKind;
  enabled: boolean;
  /** Por qué está deshabilitada (se muestra como ayuda). */
  disabledReason?: string;
  stepId?: string;
}

/** Pasos que no se reintentan a mano: el envío y la confirmación se resuelven consultando Bre-B. */
const NOT_MANUALLY_RETRYABLE: ReadonlySet<SagaStep> = new Set(['SEND', 'AWAIT_CONFIRMATION']);
const TERMINAL_FAILURES = new Set(['FAILED', 'CANCELLED', 'EXPIRED']);

/** Último intento de cada paso (los intentos anteriores quedan en el timeline). */
export function latestAttempts(saga: readonly SagaStepDetail[]): Map<SagaStep, SagaStepDetail> {
  const map = new Map<SagaStep, SagaStepDetail>();
  for (const step of saga) map.set(step.step, step);
  return map;
}

export function isRetryable(detail: AdminPaymentIntentDetail, step: SagaStepDetail): boolean {
  if (TERMINAL_FAILURES.has(detail.intent.status)) return false;
  if (!step.idempotent || NOT_MANUALLY_RETRYABLE.has(step.step)) return false;
  if (step.status !== 'UNKNOWN' && step.status !== 'FAILED') return false;
  return latestAttempts(detail.saga).get(step.step)?.id === step.id;
}

export function operatorActions(detail: AdminPaymentIntentDetail): OperatorAction[] {
  const actions: OperatorAction[] = [];
  for (const step of detail.saga) {
    if (isRetryable(detail, step)) actions.push({ kind: 'retry', enabled: true, stepId: step.id });
  }
  const pending = detail.intent.status === 'PENDING_CONFIRMATION';
  if (pending) {
    actions.push({ kind: 'query-breb', enabled: true });
    const { status } = detail.breb;
    const confirmedAbsent = status === 'NOT_FOUND' || status === 'REJECTED';
    actions.push({
      kind: 'force-compensation',
      enabled: confirmedAbsent,
      ...(!confirmedAbsent && {
        disabledReason:
          status === 'COMPLETED'
            ? 'Bloqueada: Bre-B confirmó que el pago llegó al comercio.'
            : 'Primero consulta Bre-B: solo se compensa si confirma rechazo o que la transferencia no existe.',
      }),
    });
  }
  if (detail.openException) actions.push({ kind: 'resolve', enabled: true });
  return actions;
}
